'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { formatCountdown } from '@/lib/format/countdown';
import { cn } from '@/lib/ui/utils';
interface CountdownProps {
  targetMs: number;
  /**
   * The clock the first paint counts from. `null` means the caller has not read
   * the viewer's clock yet (it only does so after mount, so the server's markup
   * and the first client paint agree) — the countdown then starts itself.
   */
  initialNowMs: number | null;
  endedLabel: string;
  ariaLabel: (countdown: string) => string;
  className?: string;
  urgentClassName?: string;
}
const TICK_MS = 1_000;
export function Countdown(t0) {
  const $ = _c(17);
  const {
    targetMs,
    initialNowMs,
    endedLabel,
    ariaLabel,
    className,
    urgentClassName: t1
  } = t0;
  const urgentClassName = t1 === undefined ? "text-feedback-error-content motion-safe:animate-pulse" : t1;
  const initialRemainingMs = initialNowMs === null ? null : Math.max(0, targetMs - initialNowMs);
  const [remainingMs, setRemainingMs] = useState(initialRemainingMs);
  let t2;
  let t3;
  if ($[0] !== initialNowMs || $[1] !== initialRemainingMs || $[2] !== targetMs) {
    t2 = () => {
      if (initialNowMs === null) {
        return;
      }
      const baseNowMs = initialNowMs;
      const startedAt = performance.now();
      let interval;
      const updateRemaining = function updateRemaining() {
        const elapsedMs = performance.now() - startedAt;
        const nextRemainingMs = Math.max(0, targetMs - (baseNowMs + elapsedMs));
        setRemainingMs(nextRemainingMs);
        if (nextRemainingMs === 0 && interval !== undefined) {
          window.clearInterval(interval);
          interval = undefined;
        }
      };
      updateRemaining();
      if (initialRemainingMs === 0) {
        return;
      }
      interval = window.setInterval(updateRemaining, TICK_MS);
      return () => {
        if (interval !== undefined) {
          window.clearInterval(interval);
        }
      };
    };
    t3 = [initialNowMs, initialRemainingMs, targetMs];
    $[0] = initialNowMs;
    $[1] = initialRemainingMs;
    $[2] = targetMs;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== remainingMs) {
    t4 = remainingMs === null ? null : formatCountdown(remainingMs);
    $[5] = remainingMs;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const value = t4;
  const text = value === null ? "\xA0" : value.ended ? endedLabel : value.text;
  let t5;
  if ($[7] !== ariaLabel || $[8] !== text) {
    t5 = ariaLabel(text);
    $[7] = ariaLabel;
    $[8] = text;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = value?.urgent === true && urgentClassName;
  let t7;
  if ($[10] !== className || $[11] !== t6) {
    t7 = cn(className, t6);
    $[10] = className;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7 || $[15] !== text) {
    t8 = <span data-testid="countdown" role="timer" aria-live="polite" aria-label={t5} className={t7}>{text}</span>;
    $[13] = t5;
    $[14] = t7;
    $[15] = text;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}